import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card, C } from "@/components/district/kit";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis, CartesianGrid, Legend as RLegend } from "recharts";
import { CaretRight, CircleNotch, HandHeart, Globe, UsersThree, Medal, DownloadSimple, X } from "@phosphor-icons/react";
import { useT } from "@/i18n";

const usd = (n) => "$" + (Number(n) || 0).toLocaleString();
const short = (n) => (n || "").replace(/^rotary club of\s*/i, "").replace(/^rotary\s*/i, "");
const AG_COLORS = ["#2B77C4", "#0E8C99", "#B3175A", "#B07D10", "#7C4DFF", "#0F8A5F"];

// Share one in-flight rollup request across the (up to four) GivingPanel instances
// rendered on a page, and briefly cache the result so the insight cards stay populated
// when the heavier district payload lands and the panels re-mount (no reload flash).
let _rollupInflight = null;
let _rollupCache = null;
let _rollupCacheAt = 0;
const _ROLLUP_TTL_MS = 30000;
function fetchRollup() {
  if (_rollupCache && (Date.now() - _rollupCacheAt) < _ROLLUP_TTL_MS) return Promise.resolve(_rollupCache);
  if (!_rollupInflight) {
    _rollupInflight = api.get("/dashboard/foundation-rollup")
      .then((r) => { _rollupCache = r.data; _rollupCacheAt = Date.now(); return r.data; })
      .finally(() => { _rollupInflight = null; });
  }
  return _rollupInflight;
}

const FUND_META = [
  ["annual_fund", "club_annual_fund", C.indigo],
  ["polio_plus", "gp_polio", C.turquoise],
  ["other_funds", "club_other_funds", C.gold],
  ["endowment", "gp_endowment_short", C.cranberry],
];
const G_ORDER = ["Male", "Female", "Not given", "Self-described"];
const G_COLORS = { "Male": C.indigo, "Female": C.cranberry, "Not given": "#9AA3AF", "Self-described": C.turquoise };
const G_PALETTE = [C.indigo, C.cranberry, C.turquoise, C.gold, "#8A5A00", "#1E7A46", "#4B31C9", "#B36AE2"];
const orderGKeys = (keys) => [...G_ORDER.filter((k) => keys.includes(k)), ...keys.filter((k) => !G_ORDER.includes(k)).sort()];
const gColor = (k, i) => G_COLORS[k] || G_PALETTE[i % G_PALETTE.length];
const REC_BARS = [["phf", "PHF"], ["md1", "MD1"], ["md2", "MD2"], ["md3", "MD3"], ["md4", "MD4"]];
const REC_CIRCLES = [["Bequest Society", ["bq1", "bq2", "bq3", "bq4"], C.turquoise], ["Arch Klumph Society", ["aks_tc", "aks_ptc"], C.cranberry]];
const REC_CIRCLES4 = [
  ["PHF", ["phf"], C.indigo],
  ["Major Donors", ["md1", "md2", "md3", "md4"], C.gold],
  ["Bequest Society", ["bq1", "bq2", "bq3", "bq4"], C.turquoise],
  ["AKS", ["aks_tc", "aks_ptc"], C.cranberry],
];
const REC_COLS = [["PHF", ["phf"]], ["MD1", ["md1"]], ["MD2", ["md2"]], ["MD3", ["md3"]], ["MD4", ["md4"]], ["Bequest", ["bq1", "bq2", "bq3", "bq4"]], ["AKS", ["aks_tc", "aks_ptc"]]];
const sumRec = (src, keys, fld) => keys.reduce((a, k) => a + ((src[k] || {})[fld] || 0), 0);

function CircleStat({ label, color, prev, year }) {
  return (
    <div className="flex flex-col items-center">
      <div className="grid place-items-center rounded-full" style={{ width: 88, height: 88, background: color + "1A", border: `3px solid ${color}` }}>
        <span className="text-[26px] font-extrabold leading-none" style={{ color: C.ink }}>{year}</span>
      </div>
      <p className="mt-2 text-center text-[15px] font-bold leading-tight" style={{ color: C.ink }}>{label}</p>
      <div className="mt-1.5 flex items-start justify-center gap-7 text-center">
        <div>
          <p className="text-[16px] font-extrabold leading-none" style={{ color: C.ink }}>{year}</p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>This Year</p>
        </div>
        <div>
          <p className="text-[16px] font-extrabold leading-none" style={{ color: C.ink }}>{prev}</p>
          <p className="mt-1 text-[10px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Upto Last Year</p>
        </div>
      </div>
    </div>
  );
}

function RecognitionsCard({ scope, scopeLabel, inZone }) {
  const { t } = useT();
  const recog = scope.recognitions || {};
  return (
    <Card data-testid="recognitions-panel" className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <Medal size={22} weight="fill" style={{ color: C.gold }} />
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{inZone ? t("club_recognitions") : "TRF Giving Recognitions"}</h2>
          <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{t("gp_rec_sub")} · {scopeLabel}</p>
        </div>
      </div>
      <div className="mt-5 grid grid-cols-2 gap-x-4 gap-y-6" data-testid="recognitions-circles">
        {REC_CIRCLES4.map(([label, keys, color]) => (
          <CircleStat key={label} label={label} color={color} prev={sumRec(recog, keys, "prev")} year={sumRec(recog, keys, "year")} />
        ))}
      </div>
    </Card>
  );
}

function RecognitionsPerClubTable({ selectedZoneName, zoneClubs, onOpenClub }) {
  const { t } = useT();
  const [period, setPeriod] = useState("year");
  return (
    <Card data-testid="recognitions-club-table">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-[16px] font-bold uppercase tracking-wide" style={{ color: C.ink }}>{selectedZoneName} · Recognitions per club</h2>
        <div className="inline-flex rounded-full p-0.5" style={{ background: C.indigoSoft }} data-testid="rec-period-toggle">
          {[["year", t("club_this_year")], ["prev", t("gp_till_last_year")]].map(([v, l]) => (
            <button key={v} data-testid={`rec-period-${v}`} onClick={() => setPeriod(v)}
              className="rounded-full px-3 py-1 text-[12px] font-bold transition-colors"
              style={period === v ? { background: C.indigo, color: "#fff" } : { color: C.indigoText }}>{l}</button>
          ))}
        </div>
      </div>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[560px] text-left text-[13px]">
          <thead><tr className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>
            <th className="pb-2 pr-3">{t("dist_col_club")}</th>
            {REC_COLS.map(([s]) => <th key={s} className="pb-2 px-2 text-center">{s}</th>)}
          </tr></thead>
          <tbody>
            {zoneClubs.map((r) => {
              const click = onOpenClub ? () => onOpenClub(r) : undefined;
              return (
                <tr key={r.club_id} onClick={click} data-testid={`rec-${r.club_id}`}
                  className={`border-t ${click ? "cursor-pointer hover:bg-[#F6F4FF]" : ""}`} style={{ borderColor: C.line }}>
                  <td className="py-2 pr-3 font-semibold" style={{ color: C.ink }}>{short(r.club_name)}</td>
                  {REC_COLS.map(([s, keys]) => <td key={s} className="py-2 px-2 text-center" style={{ color: C.ink }}>{sumRec(r.recognitions || {}, keys, period)}</td>)}
                </tr>
              );
            })}
            {zoneClubs.length === 0 && <tr><td colSpan={REC_COLS.length + 1} className="py-6 text-center text-[15px] font-medium" style={{ color: C.sub }}>{t("gp_no_clubs_zone")}</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

function MembershipCard({ genderData, inductedData, genderTotal, inductedTotal, scopeLabel, inZone }) {
  const { t } = useT();
  void genderTotal; void inductedTotal;
  const Stats = ({ data, empty }) => (
    <div className="mt-1.5 flex flex-wrap justify-center gap-x-2.5 gap-y-0.5 text-[11.5px]" style={{ color: C.sub }}>
      {data.some((x) => x.value)
        ? data.filter((x) => x.value).map((x) => <span key={x.name}><b style={{ color: C.ink }}>{x.value}</b> {x.name}</span>)
        : <span>{empty}</span>}
    </div>
  );
  return (
    <Card data-testid="membership-panel" className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <UsersThree size={22} weight="fill" style={{ color: C.gold }} />
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{inZone ? t("gp_membership") : "District Membership Overview"}</h2>
          {inZone && <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{scopeLabel}</p>}
        </div>
      </div>
      <div className="mt-4 grid grid-cols-2 gap-4">
        <div className="flex flex-col text-center">
          <p className="mb-1 flex min-h-[36px] items-center justify-center text-[12px] font-bold uppercase leading-tight tracking-wide" style={{ color: C.sub }}>{t("club_total_membership")}</p>
          <Donut data={genderData} height={150} />
          <Stats data={genderData} empty={t("gp_no_data")} />
        </div>
        <div className="flex flex-col text-center">
          <p className="mb-1 flex min-h-[36px] items-center justify-center text-[12px] font-bold uppercase leading-tight tracking-wide" style={{ color: C.sub }}>{t("club_new_inducted")}</p>
          <Donut data={inductedData} height={150} />
          <Stats data={inductedData} empty={t("gp_no_inductions")} />
        </div>
      </div>
      <div className="mt-auto pt-3">
        <div className="flex flex-wrap justify-center gap-x-4 gap-y-1" data-testid="membership-legend">
          {genderData.map((g) => (
            <span key={g.name} className="flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: C.sub }}>
              <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: g.color }} />{g.name}
            </span>
          ))}
        </div>
        <p className="mt-2 text-[11px] italic" style={{ color: C.sub }}>As per the membership data received from RI — new members admitted this Rotary Year (1 Jul–30 Jun).</p>
      </div>
    </Card>
  );
}

function FoundationCard({ s, fundData, scopeLabel, inZone }) {
  const { t } = useT();
  void inZone;
  return (
    <Card data-testid="foundation-panel" className="flex h-full flex-col">
      <div className="flex items-center gap-2">
        <HandHeart size={22} weight="fill" style={{ color: C.indigo }} />
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>Total Giving</h2>
          <p className="text-[13px] font-semibold" style={{ color: C.sub }} data-testid="foundation-scope">{scopeLabel}</p>
        </div>
      </div>
      <div className="mt-4">
        <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_fund_split")}</p>
        <Donut data={fundData} center="usd" height={260} />
        <div className="mt-2"><Legend items={fundData} kind="usd" /></div>
      </div>
    </Card>
  );
}

function GrantsCard({ s, scopeLabel, inZone }) {
  const { t } = useT();
  const g = s.grants || {};
  const cell = (label, value, tinted) => (
    <div className={`rounded-2xl px-4 py-3.5 ${tinted ? "" : "border"}`} style={tinted ? { background: C.indigoSoft } : { borderColor: C.line }}>
      <p className="text-[24px] font-extrabold leading-none" style={{ color: tinted ? C.indigoText : C.ink }}>{value}</p>
      <p className="mt-1.5 text-[12px] font-semibold" style={{ color: C.sub }}>{label}</p>
    </div>
  );
  return (
    <Card data-testid="grants-panel">
      <div className="flex items-center gap-2">
        <Globe size={22} weight="fill" style={{ color: C.turquoise }} />
        <div>
          <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{inZone ? t("club_global_grants") : "Global Grants Overview"}</h2>
          {inZone && <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{scopeLabel}</p>}
        </div>
      </div>
      <div className="mt-4">
        <p className="mb-2 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Number of projects</p>
        <div className="grid grid-cols-2 gap-3">
          {cell("Carried forward", g.carried_projects || 0, true)}
          {cell("Current year", g.year_projects || 0, true)}
        </div>
      </div>
      <div className="mt-4">
        <p className="mb-2 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Project outlay</p>
        <div className="grid grid-cols-2 gap-3">
          {cell("Carried forward", usd(g.carried_outlay), false)}
          {cell("Current year", usd(g.year_outlay), false)}
        </div>
      </div>
    </Card>
  );
}

function ClubGivingTable({ selectedZoneName, zoneClubs, onOpenClub }) {
  const { t } = useT();
  return (
    <Card data-testid="foundation-club-table">
      <h2 className="text-[16px] font-bold uppercase tracking-wide" style={{ color: C.ink }}>{selectedZoneName} · {t("gp_club_giving_by_fund")}</h2>
      <div className="mt-3 overflow-x-auto">
        <table className="w-full min-w-[620px] text-left text-[14px]">
          <thead><tr className="text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>
            <th className="pb-2 pr-3">{t("dist_col_club")}</th>
            {FUND_META.map(([k, name]) => <th key={k} className="pb-2 px-3">{t(name)}</th>)}
            <th className="pb-2 pl-3">{t("gp_total_label")}</th>
          </tr></thead>
          <tbody>
            {zoneClubs.map((r) => {
              const click = onOpenClub ? () => onOpenClub(r) : undefined;
              return (
                <tr key={r.club_id} onClick={click} data-testid={`fp-${r.club_id}`}
                  className={`border-t ${click ? "cursor-pointer hover:bg-[#F6F4FF]" : ""}`} style={{ borderColor: C.line }}>
                  <td className="py-2 pr-3 font-semibold" style={{ color: C.ink }}>{short(r.club_name)}</td>
                  {FUND_META.map(([k]) => <td key={k} className="py-2 px-3" style={{ color: C.sub }}>{usd((r.funds || {})[k])}</td>)}
                  <td className="py-2 pl-3 font-bold" style={{ color: C.indigoText }}>{usd(r.foundation_total)}</td>
                </tr>
              );
            })}
            {zoneClubs.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[15px] font-medium" style={{ color: C.sub }}>{t("gp_no_clubs_zone")}</td></tr>}
          </tbody>
        </table>
      </div>
    </Card>
  );
}


function Donut({ data, height = 180, center }) {
  const { t } = useT();
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0);
  if (!total) return <div className="flex h-[140px] items-center justify-center text-[14px] font-medium" style={{ color: C.sub }}>{t("gp_no_data")}</div>;
  return (
    <div className="relative">
      <ResponsiveContainer width="100%" height={height}>
        <PieChart>
          <Pie data={data} dataKey="value" nameKey="name" cx="50%" cy="50%" innerRadius={center === "usd" ? "62%" : "55%"} outerRadius="90%" paddingAngle={2} minAngle={4} stroke="none">
            {data.map((d, i) => <Cell key={i} fill={d.color} />)}
          </Pie>
          <Tooltip formatter={(v, n) => [center === "usd" ? usd(v) : v, n]} />
        </PieChart>
      </ResponsiveContainer>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center">
        {center === "usd" ? (
          <>
            <span className="text-[16px] font-bold leading-none" style={{ color: C.ink }}>$</span>
            <span className="mt-0.5 text-[19px] font-extrabold leading-tight" style={{ color: C.ink }}>{total.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
          </>
        ) : (
          <>
            <span className="text-[20px] font-extrabold leading-none" style={{ color: C.ink }}>{total}</span>
            <span className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_total_label")}</span>
          </>
        )}
      </div>
    </div>
  );
}

function Legend({ items, kind }) {
  const total = items.reduce((s, d) => s + (Number(d.value) || 0), 0);
  return (
    <ul className="space-y-1.5">
      {items.map((d) => {
        const pct = total ? Math.round((d.value / total) * 100) : 0;
        return (
          <li key={d.name} className="flex items-center justify-between gap-3 text-[13px]">
            <span className="flex items-center gap-2 font-medium" style={{ color: C.ink }}>
              <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ background: d.color }} /> {d.name}
            </span>
            <span className="font-semibold" style={{ color: C.sub }}>
              {kind === "usd" ? usd(d.value) : d.value} <span className="text-[11px]">· {pct}%</span>
            </span>
          </li>
        );
      })}
    </ul>
  );
}

export default function GivingPanel({ selectedZoneName, onOpenClub, stacked, grid2, part }) {
  const { t } = useT();
  const [d, setD] = useState(null);
  const [agOpen, setAgOpen] = useState(false);
  useEffect(() => { fetchRollup().then((data) => setD(data)).catch(() => setD({ clubs: [] })); }, []);
  const exportXlsx = async () => {
    try {
      const res = await api.get("/dashboard/foundation-rollup/export", { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = "foundation-grants-rollup.xlsx"; a.click();
      URL.revokeObjectURL(url);
    } catch { /* ignore */ }
  };
  const inZone = !!selectedZoneName;

  const scope = useMemo(() => {
    if (!d) return null;
    if (inZone) return (d.zones || []).find((z) => z.zone_name === selectedZoneName) || null;
    return d.totals || null;
  }, [d, inZone, selectedZoneName]);

  const zoneClubs = useMemo(
    () => (inZone ? (d?.clubs || []).filter((r) => r.zone_name === selectedZoneName) : []),
    [d, inZone, selectedZoneName]
  );

  if (!d) return <Card><div className="flex items-center justify-center py-10"><CircleNotch size={22} className="animate-spin" style={{ color: C.sub }} /></div></Card>;
  if ((d.clubs || []).length === 0) return null;

  const s = scope || { foundation_total: 0, per_capita: 0, funds: {}, grants: { underway: 0, outlay: 0 }, gender: {}, inducted: {}, recognitions: {} };
  const scopeLabel = inZone ? selectedZoneName : t("gp_whole_district");
  const fundData = FUND_META.map(([k, name, color]) => ({ name: t(name), value: Number((s.funds || {})[k]) || 0, color }));
  const gkeys = orderGKeys(Object.keys({ ...(s.gender || {}), ...(s.inducted || {}) }));
  const genderData = gkeys.map((k, i) => ({ name: k, value: Number((s.gender || {})[k]) || 0, color: gColor(k, i) }));
  const inductedData = gkeys.map((k, i) => ({ name: k, value: Number((s.inducted || {})[k]) || 0, color: gColor(k, i) }));
  const inductedTotal = inductedData.reduce((a, b) => a + b.value, 0);
  const genderTotal = genderData.reduce((a, b) => a + b.value, 0);

  if (part === "rail") {
    return (
      <div className="space-y-5" data-testid="giving-panel-rail">
        <MembershipCard genderData={genderData} inductedData={inductedData} genderTotal={genderTotal} inductedTotal={inductedTotal} scopeLabel={scopeLabel} inZone={inZone} />
        <FoundationCard s={s} fundData={fundData} scopeLabel={scopeLabel} inZone={inZone} />
        <GrantsCard s={s} scopeLabel={scopeLabel} inZone={inZone} />
      </div>
    );
  }
  if (part === "under") {
    return (
      <div className="grid items-start gap-6 md:grid-cols-2" data-testid="giving-panel-under">
        <ClubGivingTable selectedZoneName={selectedZoneName} zoneClubs={zoneClubs} onOpenClub={onOpenClub} />
        <RecognitionsPerClubTable selectedZoneName={selectedZoneName} zoneClubs={zoneClubs} onOpenClub={onOpenClub} />
      </div>
    );
  }

  if (part === "membership") return <MembershipCard genderData={genderData} inductedData={inductedData} genderTotal={genderTotal} inductedTotal={inductedTotal} scopeLabel={scopeLabel} inZone={inZone} />;
  if (part === "foundation") return <FoundationCard s={s} fundData={fundData} scopeLabel={scopeLabel} inZone={inZone} />;
  if (part === "grants") return <GrantsCard s={s} scopeLabel={scopeLabel} inZone={inZone} />;
  if (part === "recognitions") return <RecognitionsCard scope={s} scopeLabel={scopeLabel} inZone={inZone} zoneClubs={zoneClubs} onOpenClub={onOpenClub} />;

  if (part === "ag") {
    if (inZone || !d.has_current_year_giving || (d.ags || []).length === 0) return null;
    const ags = d.ags || [];
    const top = [...ags].sort((a, b) => (b.foundation_total || 0) - (a.foundation_total || 0)).slice(0, 3);
    const AgCard = (a, i) => (
      <div key={a.ag_name} data-testid={`ag-row-${a.ag_name}`} className="rounded-2xl border-2 p-4 text-center" style={{ borderColor: AG_COLORS[i % 6], background: AG_COLORS[i % 6] + "0D" }}>
        <p className="text-[15px] font-bold leading-tight" style={{ color: C.ink }}>{a.ag_name}</p>
        <div className="mt-2.5 grid grid-cols-3 gap-2 text-center">
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_membership")}</p>
            <p className="mt-0.5 text-[16px] font-extrabold" style={{ color: C.ink }}>{a.members}</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>Total Giving</p>
            <p className="mt-0.5 text-[16px] font-extrabold" style={{ color: C.indigoText }}>{usd(a.foundation_total)}</p>
          </div>
          <div>
            <p className="text-[11px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_per_capita")}</p>
            <p className="mt-0.5 text-[16px] font-extrabold" style={{ color: C.ink }}>{usd(a.per_capita)}</p>
          </div>
        </div>
      </div>
    );
    return (
      <>
        {/* Compact teaser — full list lives in a drawer to keep the page short */}
        <Card data-testid="ag-group-rollup">
          <div className="flex items-start justify-between gap-3">
            <div>
              <h2 className="text-[16px] font-bold tracking-tight" style={{ color: C.ink }}>{t("gp_by_ag")}</h2>
              <p className="mt-0.5 text-[13px] font-semibold" style={{ color: C.sub }}>{ags.length} {t("gp_ags_label")}</p>
            </div>
            <button data-testid="ag-drawer-open" onClick={() => setAgOpen(true)}
              className="inline-flex shrink-0 items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold text-white" style={{ background: C.indigo }}>
              {t("gp_view_all")} <CaretRight size={15} weight="bold" />
            </button>
          </div>
          <div className="mt-4 space-y-2">
            {top.map((a, i) => (
              <div key={a.ag_name} className="flex items-center justify-between gap-3 rounded-xl px-3 py-2.5" style={{ background: AG_COLORS[i % 6] + "0D" }}>
                <span className="flex min-w-0 items-center gap-2">
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: AG_COLORS[i % 6] }} />
                  <span className="truncate text-[14px] font-bold" style={{ color: C.ink }}>{a.ag_name}</span>
                </span>
                <span className="shrink-0 text-[14px] font-extrabold" style={{ color: C.indigoText }}>{usd(a.foundation_total)}</span>
              </div>
            ))}
          </div>
        </Card>

        {/* Drawer: right slide-over on desktop, bottom sheet on mobile */}
        {agOpen && (
          <div className="fixed inset-0 z-50" data-testid="ag-drawer" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={() => setAgOpen(false)} data-testid="ag-drawer-overlay" />
            <div className="absolute inset-x-0 bottom-0 max-h-[85vh] rounded-t-3xl bg-[#F6F5FB] sm:inset-y-0 sm:right-0 sm:left-auto sm:max-h-none sm:w-[min(560px,92vw)] sm:rounded-none sm:rounded-l-3xl"
              style={{ boxShadow: "0 -8px 40px rgba(20,20,55,.28)" }}>
              <div className="flex items-center justify-between gap-3 border-b border-black/5 px-5 py-4">
                <div>
                  <h2 className="text-[17px] font-bold tracking-tight" style={{ color: C.ink }}>{t("gp_by_ag")}</h2>
                  <p className="text-[12.5px] font-semibold" style={{ color: C.sub }}>{ags.length} {t("gp_ags_label")}</p>
                </div>
                <button data-testid="ag-drawer-close" onClick={() => setAgOpen(false)} className="grid h-9 w-9 place-items-center rounded-full bg-white" style={{ color: C.ink }}>
                  <X size={18} weight="bold" />
                </button>
              </div>
              <div className="grid max-h-[calc(85vh-72px)] gap-3 overflow-y-auto p-5 sm:max-h-[calc(100vh-72px)] sm:grid-cols-2">
                {ags.map((a, i) => AgCard(a, i))}
              </div>
            </div>
          </div>
        )}
      </>
    );
  }

  return (
    <div className="space-y-6" data-testid="giving-panel">
      <div className="flex items-center justify-between">
        <h2 className="text-[15px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_section_title")}</h2>
        <button data-testid="rollup-export" onClick={exportXlsx}
          className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-[13px] font-bold text-white" style={{ background: C.indigo }}>
          <DownloadSimple size={15} weight="bold" /> {t("gp_export")}
        </button>
      </div>
      <div className={grid2 ? "grid items-start gap-6 md:grid-cols-2" : stacked ? "space-y-6" : "grid gap-6 lg:grid-cols-3"}>
        {/* Membership & inductees — both donuts in one box */}
        <Card data-testid="membership-panel">
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2">
              <UsersThree size={22} weight="fill" style={{ color: C.gold }} />
              <div>
                <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{t("gp_membership")}</h2>
                <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{scopeLabel}</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-x-3 gap-y-1" data-testid="membership-legend">
              {genderData.map((g) => (
                <span key={g.name} className="flex items-center gap-1.5 text-[11.5px] font-semibold" style={{ color: C.sub }}>
                  <span className="h-2.5 w-2.5 shrink-0 rounded-full" style={{ background: g.color }} />{g.name}
                </span>
              ))}
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 gap-4">
            <div className="text-center">
              <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("club_total_membership")}</p>
              <Donut data={genderData} height={150} />
              <div className="mt-1.5 flex flex-wrap justify-center gap-x-2.5 gap-y-0.5 text-[11.5px]" style={{ color: C.sub }}>
                {genderData.filter((x) => x.value).map((x) => <span key={x.name}><b style={{ color: C.ink }}>{x.value}</b> {x.name}</span>)}
              </div>
            </div>
            <div className="text-center">
              <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("club_new_inducted")}</p>
              <Donut data={inductedData} height={150} />
              <div className="mt-1.5 flex flex-wrap justify-center gap-x-2.5 gap-y-0.5 text-[11.5px]" style={{ color: C.sub }}>
                {inductedTotal > 0
                  ? inductedData.filter((x) => x.value).map((x) => <span key={x.name}><b style={{ color: C.ink }}>{x.value}</b> {x.name}</span>)
                  : <span>{t("gp_no_inductions")}</span>}
              </div>
            </div>
          </div>
          <p className="mt-2.5 text-[11px] italic" style={{ color: C.sub }}>As per the membership data received from RI — new members admitted this Rotary Year (1 Jul–30 Jun).</p>
        </Card>

        {/* Right area: center + right rails */}
        <div className={grid2 ? "contents" : stacked ? "space-y-6" : "space-y-6 lg:col-span-2"}>
          <div className={grid2 ? "contents" : stacked ? "space-y-6" : "grid gap-6 md:grid-cols-2"}>
          {/* Foundation giving */}
          <Card data-testid="foundation-panel">
          <div className="flex items-center gap-2">
            <HandHeart size={22} weight="fill" style={{ color: C.indigo }} />
            <div>
              <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{t("club_foundation_giving")}</h2>
              <p className="text-[13px] font-semibold" style={{ color: C.sub }} data-testid="foundation-scope">{scopeLabel} · {t("gp_scope_year_suffix")}</p>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2 text-[13px] font-semibold">
            <span className="rounded-lg px-3 py-1.5" style={{ background: C.indigoSoft, color: C.indigoText }}>{t("gp_total_label")} {usd(s.foundation_total)}</span>
            <span className="rounded-lg px-3 py-1.5" style={{ background: C.indigoSoft, color: C.indigoText }}>{t("gp_per_capita")} {usd(s.per_capita)}</span>
          </div>
          <div className="mt-4">
            <p className="mb-1 text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_fund_split")}</p>
            <Donut data={fundData} center="usd" height={200} />
            <div className="mt-2"><Legend items={fundData} kind="usd" /></div>
          </div>
          {!inZone && <p className="mt-3 text-[12.5px] font-medium" style={{ color: C.sub }}>{t("gp_select_zone_hint")}</p>}
        </Card>

        {/* Global Grants */}
        <Card data-testid="grants-panel">
          <div className="flex items-center gap-2">
            <Globe size={22} weight="fill" style={{ color: C.turquoise }} />
            <div>
              <h2 className="text-[18px] font-bold tracking-tight" style={{ color: C.ink }}>{t("club_global_grants")}</h2>
              <p className="text-[13px] font-semibold" style={{ color: C.sub }}>{scopeLabel}</p>
            </div>
          </div>
          <div className="mt-4 space-y-3">
            <div className="rounded-2xl px-4 py-4" style={{ background: C.indigoSoft }}>
              <p className="text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_projects_underway")}</p>
              <p className="text-[32px] font-extrabold leading-none" style={{ color: C.indigoText }} data-testid="grants-underway">{s.grants?.underway || 0}</p>
              <p className="mt-1 text-[12px]" style={{ color: C.sub }}>{t("gp_underway_sub")}</p>
            </div>
            <div className="rounded-2xl border px-4 py-4" style={{ borderColor: C.line }}>
              <p className="text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>{t("gp_total_outlay")}</p>
              <p className="text-[28px] font-extrabold leading-none" style={{ color: C.ink }} data-testid="grants-outlay">{usd(s.grants?.outlay)}</p>
              <p className="mt-1 text-[12px]" style={{ color: C.sub }}>{t("gp_outlay_sub")}</p>
            </div>
          </div>
        </Card>
          </div>

      {/* Recognitions — 4th card, completes the 2×2 */}
      <RecognitionsCard scope={s} scopeLabel={scopeLabel} inZone={inZone} zoneClubs={zoneClubs} onOpenClub={onOpenClub} />

          {/* Per-club foundation giving table — only when a zone is selected; spans full width */}
      {inZone && (
        <Card data-testid="foundation-club-table" className={grid2 ? "md:col-span-2" : ""}>
          <h2 className="text-[16px] font-bold uppercase tracking-wide" style={{ color: C.ink }}>{selectedZoneName} · {t("gp_club_giving_by_fund")}</h2>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[620px] text-left text-[14px]">
              <thead><tr className="text-[12px] font-bold uppercase tracking-wide" style={{ color: C.sub }}>
                <th className="pb-2 pr-3">{t("dist_col_club")}</th>
                {FUND_META.map(([k, name]) => <th key={k} className="pb-2 px-3">{t(name)}</th>)}
                <th className="pb-2 pl-3">{t("gp_total_label")}</th>
              </tr></thead>
              <tbody>
                {zoneClubs.map((r) => {
                  const click = onOpenClub ? () => onOpenClub(r) : undefined;
                  return (
                    <tr key={r.club_id} onClick={click} data-testid={`fp-${r.club_id}`}
                      className={`border-t ${click ? "cursor-pointer hover:bg-[#F6F4FF]" : ""}`} style={{ borderColor: C.line }}>
                      <td className="py-2 pr-3 font-semibold" style={{ color: C.ink }}>{short(r.club_name)}</td>
                      {FUND_META.map(([k]) => <td key={k} className="py-2 px-3" style={{ color: C.sub }}>{usd((r.funds || {})[k])}</td>)}
                      <td className="py-2 pl-3 font-bold" style={{ color: C.indigoText }}>{usd(r.foundation_total)}</td>
                    </tr>
                  );
                })}
                {zoneClubs.length === 0 && <tr><td colSpan={6} className="py-6 text-center text-[15px] font-medium" style={{ color: C.sub }}>{t("gp_no_clubs_zone")}</td></tr>}
              </tbody>
            </table>
          </div>
        </Card>
      )}
        </div>
      </div>
    </div>
  );
}
